/**
 * RiskSettingsScreen — every setting that changes a risk or compliance outcome,
 * editable by Risk Management (Bill 2026-10-07: "so that she (Jessica, Risk
 * Management) can tweak settings during her testing and affect the results").
 *
 * Data: GET /v3/risk/settings (values + control metadata from the server
 * catalogue), GET /v3/risk/settings/effective (what each live reader uses right
 * now), GET /v3/risk/gates (FraudPoint gate per customer type). Writes go one
 * key at a time with a required reason; every write lands in the change log.
 *
 * Not here: disclosure templates (Disclosures area) and the LexisNexis / OFAC
 * test console (LexisNexis area). The FraudPoint cut-offs appear in both this
 * screen and the LexisNexis screen; both write the same two keys.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import {
  loadRiskEffective,
  loadRiskGates,
  loadRiskSettings,
  type RiskEffective,
  type RiskGateRow,
  type RiskSettingGroup,
  type RiskSettingRow,
} from '@/services/riskSettingsApi';

import { ChangeLog } from './ChangeLog';
import { GateStrategyCard } from './GateStrategyCard';
import { SettingGroupCard } from './SettingGroupCard';
import './riskSettings.css';

const GROUPS: ReadonlyArray<{ id: RiskSettingGroup; title: string; intro: string }> = [
  {
    id: 'identity',
    title: 'Identity screening',
    intro: 'LexisNexis FraudPoint cut-offs, what an identity check needs before it runs, what bankers see in the report, and OFAC re-screening.',
  },
  {
    id: 'sanctions',
    title: 'Sanctions screening',
    intro: 'The bank screens every name on its own copy of the government sanctions lists. Which screening decides, what each list can do, the hit and review score lines, weak aliases, date-of-birth and country differences, and the Core Director forecast. Lists, tests and calibration: Home → Sanctions screening.',
  },
  {
    id: 'compliance',
    title: 'Compliance gates',
    intro: 'How the system enforces the PEP control, the FraudPoint follow-up gate, and changes to an application after its documents exist.',
  },
  {
    id: 'signing',
    title: 'Signing and disclosures',
    intro: 'How no-signature disclosures appear in the signing session, whether accounts wait for every signature, and the remote signing time limits.',
  },
  {
    // CHG-0141 (Bill 2026-11-02): one switch per role; a role that is off cannot see or print the report.
    id: 'lnReportAccess',
    title: 'Report access',
    intro: 'Which roles can see and print the Identity Verification (LexisNexis) and Sanctions Screening report PDFs, in the officer app and this panel. Global Admin always can. A role that is off cannot open either report.',
  },
];

type Tab = 'settings' | 'log';

export function RiskSettingsScreen(): JSX.Element {
  const [tab, setTab] = useState<Tab>('settings');
  const [rows, setRows] = useState<ReadonlyArray<RiskSettingRow>>([]);
  const [effective, setEffective] = useState<RiskEffective | null>(null);
  const [gates, setGates] = useState<ReadonlyArray<RiskGateRow>>([]);
  const [gatesTakeEffect, setGatesTakeEffect] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [s, e, g] = await Promise.all([loadRiskSettings(), loadRiskEffective(), loadRiskGates()]);
      setRows(s);
      setEffective(e);
      setGates(g.gates);
      setGatesTakeEffect(g.takesEffect);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleSaved = async (message: string): Promise<void> => {
    if (message) { setError(null); setSaved(message); }
    await refresh();
    setVersion((v) => v + 1);
  };
  const handleError = (message: string): void => { setSaved(null); setError(message); };

  return (
    <>
      <Link className="back-link" to="/">← Home</Link>
      <section className="page-header">
        <h1 className="page-header__title">Risk settings</h1>
        <p className="page-header__subtitle">
          Settings that change risk and compliance results in account opening. Every change needs a reason and is
          recorded in the change log. "In use now" is read back from the code that uses each setting.
        </p>
      </section>

      <nav className="subnav rs-tabs" data-tour="rs-tabs" role="tablist" aria-label="Risk settings views">
        <button type="button" role="tab" aria-selected={tab === 'settings'} onClick={() => setTab('settings')}>Settings</button>
        <button type="button" role="tab" aria-selected={tab === 'log'} onClick={() => setTab('log')}>Change log</button>
      </nav>

      {error && <section className="banner banner--err" role="alert">{error}</section>}
      {saved && <section className="banner banner--info" role="status">{saved}</section>}

      {tab === 'settings' && (
        loading ? <p className="empty">Loading settings…</p> : (
          <>
            {effective && (
              <p className="form-hint" style={{ marginTop: 0 }}>
                Values read back {new Date(effective.readAt).toLocaleTimeString()}.
              </p>
            )}
            {GROUPS.map((g) => {
              const groupRows = rows.filter((r) => r.group === g.id);
              return (
                <div key={g.id}>
                  {groupRows.length > 0 && (
                    <SettingGroupCard
                      title={g.title}
                      intro={g.intro}
                      rows={groupRows}
                      effective={effective?.settings ?? {}}
                      onSaved={handleSaved}
                      onError={handleError}
                    />
                  )}
                  {g.id === 'compliance' && gates.length > 0 && (
                    <section className="card rs-group">
                      <div className="card-header"><h2>Compliance gate enforcement</h2></div>
                      <p className="rs-group__intro">
                        Per customer type. Only gates whose setting the system acts on are listed.
                      </p>
                      <GateStrategyCard
                        gates={gates}
                        effective={effective?.gates ?? {}}
                        takesEffect={gatesTakeEffect}
                        onSaved={handleSaved}
                        onError={handleError}
                      />
                    </section>
                  )}
                </div>
              );
            })}
          </>
        )
      )}

      {tab === 'log' && <ChangeLog rows={rows} version={version} />}
    </>
  );
}
